import { Divider, List, ListItem, ListItemButton, Radio, styled } from '@mui/material';
import { useMemo } from 'react';
import { Box, Checkbox, StyledInput, Tooltip } from '../../..';
import { MenuItem } from '../../../../pages/Sspm/components/SspmComponent/Pages/Risks/RiskFilter/filters/UseFiltersData';

type Item<ItemData> = {
  value: string | JSX.Element;
  isSelected: boolean;
  itemData?: ItemData;
};

export type Items<ItemData> = {
  [key: string | number]: Item<ItemData>;
};

export type SelectMenuProps<I extends Items<ItemData>, Keys extends keyof I, ItemData> = {
  items: I;
  onSelect: (value: Keys) => void;
  isDisableField?: (item: MenuItem) => boolean;
  tooltip?: (item: MenuItem) => string | JSX.Element;
} & ({ singleSelect: true } | { singleSelect?: false; onSelectAll?: () => void; disableSelectAll?: boolean }) &
  ({ withSearch?: false } | { withSearch: true; onSearchChange: (value: string) => void; searchValue: string });

type SelectionModes = 'all' | 'none' | 'some';

const ListItemButtonStyled = styled(ListItemButton)({
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gap: '12px',
  padding: '0 24px 0 10px'
});

export const SelectFilterMenu = <
  CurrentItems extends Items<ItemData>,
  Keys extends keyof CurrentItems,
  ItemData extends { extraData?: MenuItem }
>(
  props: SelectMenuProps<CurrentItems, Keys, ItemData>
) => {
  const { items, onSelect, singleSelect, withSearch, isDisableField, tooltip } = props;
  const selectionMode = useMemo<SelectionModes>(() => {
    const selectedItems = Object.values(items).filter(item => item.isSelected);
    if (selectedItems.length === 0) {
      return 'none';
    }
    if (selectedItems.length === Object.values(items).length) {
      return 'all';
    }
    return 'some';
  }, [items]);

  const filterItems = useMemo(
    () =>
      Object.entries(items)
        .map(([key, item]) => ({
          isDisable: item?.itemData?.extraData && isDisableField ? isDisableField(item.itemData.extraData) : false,
          valueName: item?.itemData?.extraData?.text ?? '',
          key,
          item,
          tooltip: item?.itemData?.extraData && tooltip ? tooltip(item.itemData.extraData) : ''
        }))
        .sort((a, b) =>
          a.isDisable && !b.isDisable ? 1 : !a.isDisable && b.isDisable ? -1 : a.valueName.localeCompare(b.valueName)
        )
        .map(({ key, item, isDisable, tooltip }) => {
          const listItem = (
            <ListItem disablePadding key={String(key)}>
              <ListItemButtonStyled
                onClick={() => {
                  onSelect(key as Keys);
                }}
                disabled={isDisable}
              >
                {!singleSelect ? (
                  <Checkbox checked={item.isSelected} size="small" disableRipple />
                ) : (
                  <Radio checked={item.isSelected} />
                )}
                <div>{item.value}</div>
              </ListItemButtonStyled>
            </ListItem>
          );

          if (tooltip) {
            return (
              <Tooltip title={tooltip} placement="right">
                {listItem}
              </Tooltip>
            );
          }

          return listItem;
        }),
    [items, onSelect, singleSelect, isDisableField, tooltip]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 360, height: '100%' }}>
      {withSearch && (
        <StyledInput
          value={props.searchValue || ''}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
            props.onSearchChange?.(e.currentTarget.value)
          }
          placeholder="Search"
          sx={{ padding: '10px 12px' }}
          variant="outlined"
          fullWidth
        />
      )}
      {!singleSelect && !props.disableSelectAll && (
        <nav aria-label="all">
          <List>
            <ListItem disablePadding>
              <ListItemButtonStyled
                onClick={() => {
                  props.onSelectAll?.();
                }}
              >
                <Checkbox
                  checked={selectionMode !== 'none'}
                  indeterminate={selectionMode === 'some'}
                  size="small"
                  disableRipple
                />
                <div>All</div>
              </ListItemButtonStyled>
            </ListItem>
          </List>
        </nav>
      )}
      <Divider />
      <nav aria-label="filters">
        <List
          sx={{
            maxHeight: '50vh'
          }}
        >
          {filterItems}
        </List>
      </nav>
    </Box>
  );
};
